
import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ProductItem } from "./product-item"
import { Button } from "@/components/ui/button"
import { Package, Save } from "lucide-react"

export interface GTINProduct {
  id: string
  cEAN: string
  xProd: string
  uCom: string
  ncm: string
  cest: string | null
  status: string | null
  qntd: number
  isEdited?: boolean
}

const initialProducts: GTINProduct[] = [
  {
    id: "1",
    cEAN: "7891234567890",
    xProd: "Parafuso Phillips 6mm",
    uCom: "UN",
    ncm: "73181500",
    cest: "10.001.00",
    status: null,
    qntd: 500,
  },
  {
    id: "2",
    cEAN: "7891234567891",
    xProd: "Arruela Lisa 8mm",
    uCom: "UN",
    ncm: "73182100",
    cest: null,
    status: null,
    qntd: 1000,
  },
  {
    id: "3",
    cEAN: "7891234567892",
    xProd: "Porca Sextavada M10",
    uCom: "UN",
    ncm: "73181600",
    cest: "10.002.00",
    status: null,
    qntd: 0,
  },
  {
    id: "4",
    cEAN: "7891234567893",
    xProd: "Cabo de Aço 6mm",
    uCom: "M",
    ncm: "73121000",
    cest: null,
    status: null,
    qntd: 100,
  },
]

type ProductQuantityCheckerProps = {
  products?: GTINProduct[]
  isSaving?: boolean
  title?: string
  description?: string
  onSaveAll?: (products: GTINProduct[]) => Promise<void> | void
}

export function ProductQuantityChecker({
  products: externalProducts,
  isSaving = false,
  title = "Conferência de Produtos",
  description = "Verifique e corrija as quantidades totais dos produtos por grupo",
  onSaveAll
}: ProductQuantityCheckerProps) {
  const [products, setProducts] = useState<GTINProduct[]>(externalProducts ?? initialProducts)

  useEffect(() => {
    setProducts(externalProducts ?? initialProducts)
  }, [externalProducts])

  const handleQuantityChange = (id: string, quantity: number) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id === id) {
          return { ...product, qntd: quantity, isEdited: true }
        }
        return product
      }),
    )
  }

  const handleSaveAll = async () => {
    if (onSaveAll) {
      await onSaveAll(products)
      return
    }

    alert("Quantidades salvas com sucesso!")
  }

  const editedCount = products.filter((p) => p.isEdited).length
  const pendingCount = products.filter((p) => p.qntd === 0 && !p.isEdited).length

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary">
              <Package className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 rounded-lg bg-muted px-4 py-2">
              <span className="text-sm font-medium">{products.length} Produtos</span>
            </div>
            {editedCount > 0 && (
              <div className="flex items-center gap-2 rounded-lg bg-success/10 px-4 py-2">
                <div className="h-2 w-2 rounded-full bg-success" />
                <span className="text-sm font-medium text-success">{editedCount} Editados</span>
              </div>
            )}
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 rounded-lg bg-accent/10 px-4 py-2">
                <div className="h-2 w-2 rounded-full bg-accent" />
                <span className="text-sm font-medium text-accent">{pendingCount} Sem quantidade</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Product List */}
      <div className="space-y-3">
        {products.map((product) => (
          <ProductItem
            key={product.id}
            product={product}
            onQuantityChange={(qty) => handleQuantityChange(product.id, qty)}
          />
        ))}
      </div>

      {/* Save Button */}
      <div className="sticky bottom-4 flex justify-end">
        <Button size="lg" onClick={handleSaveAll} disabled={isSaving} className="gap-2 shadow-lg">
          {isSaving ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Salvar Alterações
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
